import { Injectable } from '@angular/core';
import {
  AskBidMidNumber,
  ForwardPointsField,
  GetForwardPointsResponse,
  GetImpliedVolResponse,
  IForwardPointsRequest,
  Payload,
  PricingLatestValuesRequests
} from '@approot/models';
import {
  ForwardPoints,
  LegService,
  LegSyncService,
  MarketDataService,
  PricingExtraService,
  ProductsConfigService
} from '@approot/services';
import {
  DateUtilities,
  SubscriptionSink,
  Utilities,
  clone
} from '@approot/utilities';
import { Observable, Subscription } from 'rxjs';
import { tap } from 'rxjs/operators';
import { DEFAULT_DECIMAL_PLACES } from '../leg.component';
import { getAttributeControlFromKey } from '../utilities/leg-attribute-utilities';
import {
  addTooltipIfExpired,
  clearTextColor,
  getColorIfExpired,
  getDecimalPlaces,
  isExpired,
  isGettingALatestStrike,
  markFieldAsPending,
  removePendingFromField
} from '../utilities/leg.utilities';
import { volatilityPayload } from '../utilities/payload-creation';
import { setBidAskValue } from '../utilities/vols-fwds-utilities';
import { LegHelperService } from './leg-helper.service';

type ImpliedVolPayload = {
  askBidMidNumber: AskBidMidNumber;
  timestamp: string;
  volNumber: string;
  shouldEventBeEmitted: boolean;
  setOnly?: string;
  isComposite?: boolean;
  currenciesCorrelationUsed?: number;
};

type SetImpliedVolPayload = {
  val: number;
  timestamp: string;
  volNumber: string;
  shouldEventBeEmitted: boolean;
  position: string;
  setOnly?: string;
  isComposite?: boolean;
  currenciesCorrelationUsed?: number;
};

@Injectable({
  providedIn: 'root'
})
export class LegFwdVolService {
  public forwardPointsSink = new SubscriptionSink();
  public fwdPointsChangesSub: Subscription;

  constructor(
    private legHelperService: LegHelperService,
    private legService: LegService,
    private pricingExtraService: PricingExtraService,
    private legSyncService: LegSyncService,
    private productsConfigService: ProductsConfigService,
    private marketDataService: MarketDataService
  ) {}

  public getForwardPointRequest(): IForwardPointsRequest {
    const form = this.legHelperService.form;
    if (
      form.get('expiryDate')?.valid &&
      form.get('cross')?.valid &&
      form.get('forwardPointsBid') &&
      form.get('forwardPointsAsk')
    ) {
      const expiryDate = form.get('expiryDate').value;
      return {
        request: this.marketDataService.getForwardPoints(
          [expiryDate],
          form.get('cross').value,
          this.pricingExtraService.getMarketDataTimeStamp
        ),
        date: DateUtilities.prettyDateToDate(expiryDate)
      };
    } else {
      return null;
    }
  }

  public createForwardPointsSyncChanges(
    from: 'Bid' | 'Ask',
    expirationDate: string
  ): void {
    const form = this.legHelperService.form;

    this.forwardPointsSink.sink = form
      .get(`forwardPoints${from}`)
      .valueChanges.subscribe(val => {
        let forwardPoints: ForwardPoints;
        if (from === 'Bid') {
          forwardPoints = {
            bid: val,
            ask: null,
            bidManual:
              this.legHelperService.leg.manuallyEnteredForwardPointsBid,
            askManual: null
          };
        } else {
          forwardPoints = {
            bid: null,
            ask: val,
            bidManual: null,
            askManual: this.legHelperService.leg.manuallyEnteredForwardPointsAsk
          };
        }

        this.legSyncService.updateForwardPoints({
          currencyPair: form.get('cross').value,
          expirationDate,
          forwardPoints,
          srcLeg: this.legHelperService.legIndex
        });
      });
  }

  public subTofwdPointsChanges(): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const forwardPointRequest = this.getForwardPointRequest();
    if (!forwardPointRequest) {
      return;
    }
    this.fwdPointsChangesSub?.unsubscribe();
    this.fwdPointsChangesSub = this.legSyncService
      .subscribeToForwardPointsChanges({
        currencyPair: form.get('cross').value,
        expirationDate: forwardPointRequest.date,
        srcLeg: this.legHelperService.legIndex
      })
      .subscribe({
        next: ({ bid, ask }) => {
          const askDP = getDecimalPlaces(
            'forwardPointsAsk',
            leg.attributesAsFormControls
          );
          const bidDP = getDecimalPlaces(
            'forwardPointsBid',
            leg.attributesAsFormControls
          );

          setBidAskValue(form, leg, 'forwardPointsBid', bid, bidDP);
          setBidAskValue(form, leg, 'forwardPointsAsk', ask, askDP);
        },
        error: err => console.error('subTofwdPointsChanges', err)
      });
    this.forwardPointsSink.sink = this.fwdPointsChangesSub;
  }

  /**
   * In RFQ mode, we sync the forward points across matching legs (same date, cross)
   */
  public setupForwardPointsSync(forwardPointRequest: IForwardPointsRequest) {
    if (
      !this.legHelperService.legSyncingInfo?.forwardPoints ||
      !forwardPointRequest?.request
    ) {
      return;
    }
    this.forwardPointsSink?.unsubscribe();
    this.forwardPointsSink = new SubscriptionSink();
    this.legHelperService.subSink.sink = this.forwardPointsSink;
    this.createForwardPointsSyncChanges('Bid', forwardPointRequest.date);
    this.createForwardPointsSyncChanges('Ask', forwardPointRequest.date);
    this.subTofwdPointsChanges();
  }

  public syncForwardPoints(): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    if (this.legHelperService.legSyncingInfo?.forwardPoints) {
      const forwardPointRequest = this.getForwardPointRequest();
      this.setupForwardPointsSync(forwardPointRequest);

      const askDP = getDecimalPlaces(
        'forwardPointsAsk',
        leg.attributesAsFormControls
      );
      const bidDP = getDecimalPlaces(
        'forwardPointsBid',
        leg.attributesAsFormControls
      );
      setBidAskValue(
        form,
        leg,
        'forwardPointsBid',
        leg.preSetPosition?.forwardPointsBid,
        bidDP,
        true
      );
      setBidAskValue(
        form,
        leg,
        'forwardPointsAsk',
        leg.preSetPosition?.forwardPointsAsk,
        askDP,
        true
      );
    }
  }

  private setImpliedVol(payload: SetImpliedVolPayload): void {
    const {
      val,
      timestamp,
      volNumber,
      shouldEventBeEmitted,
      position,
      setOnly,
      isComposite,
      currenciesCorrelationUsed
    } = payload;
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    const fieldName = 'vol' + volNumber + position;
    if (!form.get(fieldName)) {
      return;
    }
    if (!setOnly || setOnly.includes(position)) {
      removePendingFromField(form, leg, rfqExecutionSettings, fieldName);

      let color: string = null;
      const limit =
        (Utilities.currentEnvironment() === 'prod' ? 5 : 10) * 60_000;
      if (isComposite || isExpired(timestamp, limit)) {
        color = Utilities.orangeTextColor;
      }

      clearTextColor(leg, fieldName, color);

      if (val != null) {
        const attribute = getAttributeControlFromKey(
          fieldName,
          leg.attributesAsFormControls
        );
        leg.usingLatestValues[fieldName] = true;
        const formattedValue = (val * 100).toFixed(
          attribute.decimalPlaces ?? DEFAULT_DECIMAL_PLACES
        );
        form
          .get(fieldName)
          .setValue(`${formattedValue}%`, { emitEvent: false });
      }

      let extra: string = null;
      if (currenciesCorrelationUsed != null) {
        extra = `\x0aComposite Vol (${Math.round(
          currenciesCorrelationUsed * 100
        )}% correlation)`;
      }

      addTooltipIfExpired(fieldName, timestamp, leg, extra, limit);
    }
    if (shouldEventBeEmitted) {
      this.legHelperService.onChangeInLegFn();
    }
  }

  public handleImpliedVolatilityResponse(payload: ImpliedVolPayload): void {
    const { askBidMidNumber } = payload;
    ['Bid', 'Ask'].forEach(position => {
      const val = askBidMidNumber[position.toLowerCase()];
      if (val == null) {
        return;
      }
      this.setImpliedVol({
        val: askBidMidNumber[position.toLowerCase()],
        position,
        ...payload
      });
    });
  }

  public getImpliedVolatilityRequest(
    volNumber: string
  ): Observable<GetImpliedVolResponse | unknown> {
    const payload = volatilityPayload(
      volNumber,
      this.legHelperService.form,
      this.legHelperService.presetsLoaded,
      this.legHelperService.legSyncingInfo?.forwardPoints,
      this.pricingExtraService.getMarketDataTimeStamp,
      this.productsConfigService.cutOffTimesAndFixingSources,
      this.pricingExtraService.marketDataSpreadingRule,
      this.legHelperService.rfqExecutionSettings
    );

    if (!payload) {
      return null;
    }
    return this.legService.marketDataService(payload);
  }

  public updateForwardPoints(
    setOnly: ForwardPointsField,
    val: number,
    timestamp: string,
    field: ForwardPointsField
  ): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    const legSyncingInfo = this.legHelperService.legSyncingInfo;
    if (!form.get(field)) {
      return;
    }
    if (!setOnly || setOnly === field) {
      removePendingFromField(form, leg, rfqExecutionSettings, field);
      clearTextColor(
        leg,
        field,
        getColorIfExpired({ valueDateTime: timestamp })
      );
      leg.usingLatestValues[field] = true;
      const dp = getDecimalPlaces(field, leg.attributesAsFormControls);
      setBidAskValue(form, leg, field, val, dp, legSyncingInfo?.forwardPoints);
    }
  }

  public handleForwardPointResponse(
    forwardPoints: AskBidMidNumber,
    timestamp: string,
    shouldEventBeEmitted: boolean,
    setOnly?: ForwardPointsField
  ): void {
    const leg = this.legHelperService.leg;
    ['forwardPointsBid', 'forwardPointsAsk'].forEach(
      (key: ForwardPointsField) => {
        const fwdVal =
          forwardPoints[key === 'forwardPointsBid' ? 'bid' : 'ask'];
        if (fwdVal == null) {
          return;
        }
        this.updateForwardPoints(setOnly, fwdVal, timestamp, key);
      }
    );

    if (shouldEventBeEmitted) {
      this.legHelperService.onChangeInLegFn();
    }

    addTooltipIfExpired('forwardPointsBid', timestamp, leg);
    addTooltipIfExpired('forwardPointsAsk', timestamp, leg);
  }

  public handleFwdsFromStrike(
    fwds: AskBidMidNumber,
    timestamp: string,
    isInPricingMode: boolean
  ): void {
    const leg = this.legHelperService.leg;
    const fwdsClone = clone(fwds);
    if (fwdsClone) {
      if (isInPricingMode) {
        if (!leg.usingLatestValues['forwardPointsBid']) {
          delete fwdsClone.bid;
        }

        if (!leg.usingLatestValues['forwardPointsAsk']) {
          delete fwdsClone.ask;
        }
      }
      this.handleForwardPointResponse(fwdsClone, timestamp, true);
    }
  }

  public handleVolsFromStrike(
    askBidMidNumber: AskBidMidNumber,
    timestamp: string,
    isInPricingMode: boolean,
    strikeNumber: string,
    isComposite?: boolean,
    currenciesCorrelationUsed?: number
  ): void {
    const leg = this.legHelperService.leg;
    const cloneVol = clone(askBidMidNumber);
    if (cloneVol) {
      if (isInPricingMode) {
        const volBid = `vol${strikeNumber}Bid`;
        const volAsk = `vol${strikeNumber}Ask`;
        if (!leg.usingLatestValues[volBid]) {
          delete cloneVol.bid;
        }

        if (!leg.usingLatestValues[volAsk]) {
          delete cloneVol.ask;
        }
      }

      this.handleImpliedVolatilityResponse({
        askBidMidNumber: cloneVol,
        timestamp,
        volNumber: strikeNumber,
        shouldEventBeEmitted: true,
        isComposite,
        currenciesCorrelationUsed
      });
    }
  }

  public calcImpliedVolatility(volNumber: string, setOnly?: string): void {
    const form = this.legHelperService.form;
    const leg = this.legHelperService.leg;
    const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
    const request = this.getImpliedVolatilityRequest(volNumber);
    if (
      !request ||
      (!this.legHelperService.presetsLoaded && leg.legAttributeDataFromTab)
    ) {
      return;
    }

    if (!setOnly || setOnly.includes('Bid')) {
      markFieldAsPending(form, leg, 'vol' + volNumber + 'Bid');
    }

    if (!setOnly || setOnly.includes('Ask')) {
      markFieldAsPending(form, leg, 'vol' + volNumber + 'Ask');
    }

    request.subscribe({
      next: (response: GetImpliedVolResponse) => {
        this.handleImpliedVolatilityResponse({
          askBidMidNumber: response.vols[0].strikeVol,
          timestamp: response.valueDateTime,
          volNumber,
          shouldEventBeEmitted: true,
          setOnly,
          isComposite: response.isComposite,
          currenciesCorrelationUsed: response.currenciesCorrelationUsed
        });
      },
      error: () => {
        if (!setOnly || setOnly.includes('Bid'))
          removePendingFromField(
            form,
            leg,
            rfqExecutionSettings,
            'vol' + volNumber + 'Bid'
          );

        if (!setOnly || setOnly.includes('Ask'))
          removePendingFromField(
            form,
            leg,
            rfqExecutionSettings,
            'vol' + volNumber + 'Ask'
          );
      }
    });
  }

  public calcForwardPoint(setOnly?: ForwardPointsField) {
    const forwardPointRequest = this.getForwardPointRequest();
    if (forwardPointRequest?.request) {
      const form = this.legHelperService.form;
      const leg = this.legHelperService.leg;
      const legSyncingInfo = this.legHelperService.legSyncingInfo;
      const rfqExecutionSettings = this.legHelperService.rfqExecutionSettings;
      let setBid = true;
      let setAsk = true;
      if (!setOnly || setOnly === 'forwardPointsBid') {
        markFieldAsPending(form, leg, 'forwardPointsBid');
        setAsk = false;
      }

      if (!setOnly || setOnly === 'forwardPointsAsk') {
        markFieldAsPending(form, leg, 'forwardPointsAsk');
        setBid = false;
      }

      forwardPointRequest.request.subscribe({
        next: (response: GetForwardPointsResponse) => {
          this.handleForwardPointResponse(
            response.forwards[forwardPointRequest.date].forwardPoints,
            '', // backend in the future will return a timestamp
            true,
            setOnly
          );
          if (legSyncingInfo?.forwardPoints) {
            const { bid, ask } =
              response.forwards[forwardPointRequest.date].forwardPoints;

            this.legSyncService.updateForwardPoints({
              expirationDate: forwardPointRequest.date,
              currencyPair: form.get('cross').value,
              forwardPoints: {
                bid: setBid ? bid?.toString() : null,
                bidManual: leg.manuallyEnteredForwardPointsBid,
                ask: setAsk ? ask?.toString() : null,
                askManual: leg.manuallyEnteredForwardPointsAsk
              },
              srcLeg: this.legHelperService.legIndex
            });
          }
        },
        error: () => {
          removePendingFromField(
            form,
            leg,
            rfqExecutionSettings,
            'forwardPointsBid'
          );
          removePendingFromField(
            form,
            leg,
            rfqExecutionSettings,
            'forwardPointsAsk'
          );
        }
      });
    }
  }

  public initForwardPointVolCalls(): void {
    const leg = this.legHelperService.leg;
    if (!isGettingALatestStrike(leg)) {
      this.calcForwardPoint();
    }

    ['', '1', '2'].forEach(num => {
      if (!leg.usingLatestValues[`strike${num}`]) {
        this.calcImpliedVolatility(num);
      }
    });
  }

  public addRequestsForLatestsImpliedVols(
    volNumber: string,
    requests: PricingLatestValuesRequests
  ): void {
    const leg = this.legHelperService.leg;
    const volAskField = `vol${volNumber}Ask`;
    const volBidField = `vol${volNumber}Bid`;
    if (leg.usingLatestValues[`strike${volNumber}`]) {
      // strike response contains vols
      return;
    }

    if (
      leg.usingLatestValues[volAskField] ||
      leg.usingLatestValues[volBidField]
    ) {
      requests['vol' + volNumber] = this.getImpliedVolatilityRequest(
        volNumber
      ).pipe(
        tap((resp: GetImpliedVolResponse) => {
          this.handleVolsFromStrike(
            resp.vols?.[0]?.strikeVol,
            resp.valueDateTime,
            true,
            volNumber,
            resp.isComposite,
            resp.currenciesCorrelationUsed
          );
        })
      );
    }
  }

  public addForwardPointsRequests(
    liveValueRequests: PricingLatestValuesRequests,
    strategy: Payload
  ): void {
    const leg = this.legHelperService.leg;

    if (
      leg.shortcutValueUsed.strike ||
      leg.shortcutValueUsed.strike1 ||
      leg.shortcutValueUsed.strike2
    ) {
      return;
    }

    if (
      leg.usingLatestValues.forwardPointsAsk ||
      leg.usingLatestValues.forwardPointsBid
    ) {
      liveValueRequests.forwardPoints =
        this.getForwardPointRequest().request.pipe(
          tap((resp: GetForwardPointsResponse) => {
            this.handleFwdsFromStrike(
              resp.forwards[strategy.products[0].expiryDate].forwardPoints,
              '',
              true
            );
          })
        );
    }
  }
}
